import { CreatePaymentAccountBody } from 'components/forms/payment-accounts/create-payment-account-form/create-payment-account-form.schema';
import { Paging } from 'components/search/search.hooks';
import {
  generateInvalidates,
  generateProvides,
  generateProvidesList,
} from 'util/generateTags';
import {
  transformDisputesData,
  transformPaymentsReportsData,
} from 'api/payments/payments.utils';
import { incApi } from '../inc-api';
import {
  AccountLink,
  CreateAccountLinkRequest,
  DisputeResponse,
  FileLink,
  PaymentAccount,
  UpdateAccount,
  UpdateAccountArgs,
  UpdateDisputeRequest,
} from './payments.types';
import { transformEvidence } from './payments.util';
import { SingleTransaction } from './transactions.types';

const endpoints = incApi.injectEndpoints({
  overrideExisting: true,
  endpoints: build => ({
    listAccounts: build.query<{ content: PaymentAccount[] } & Paging, any>({
      query: params => ({
        url: `incentivio-payments/paymentaccounts`,
        method: 'GET',
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.content,
        element => element.accountId,
        'PaymentAccount',
      ),
    }),

    getAccount: build.query<PaymentAccount, any>({
      query: id => ({
        url: `incentivio-payments/paymentaccounts/${id}`,
        method: 'GET',
      }),
      providesTags: generateProvides(
        result => result.accountId,
        'PaymentAccount',
      ),
      keepUnusedDataFor: 0,
    }),

    createAccount: build.mutation<PaymentAccount, CreatePaymentAccountBody>({
      query: body => ({
        url: `incentivio-payments/paymentaccounts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PaymentAccounts'],
    }),

    updateAccount: build.mutation<UpdateAccount, UpdateAccountArgs>({
      query: body => ({
        url: `incentivio-payments/paymentaccounts/${body.accountId}`,
        method: 'PUT',
        body: {
          ...body,
          stripeBody: {
            settings: {
              payments: {
                statement_descriptor:
                  body.stripeAccount.settings.payments.statement_descriptor,
              },
            },
          },
        },
      }),
      invalidatesTags: generateInvalidates(
        body => body.accountId,
        'PaymentAccount',
      ),
    }),

    deleteAccount: build.mutation<null, { accountId: string }>({
      query: body => ({
        url: `incentivio-payments/paymentaccounts/${body.accountId}`,
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['PaymentAccounts'],
    }),

    createAccountLink: build.mutation<AccountLink, CreateAccountLinkRequest>({
      query: ({ paymentAccountId, body }) => ({
        url: `incentivio-payments/paymentaccounts/${paymentAccountId}/accountlink`,
        method: 'POST',
        body,
      }),
      invalidatesTags: generateInvalidates(
        body => body.paymentAccountId,
        'PaymentAccountLink',
      ),
    }),

    getPaymentsReportsData: build.query({
      query: params => ({
        url: `incentivio-payments/payments/reports`,
        method: 'GET',
        params,
      }),
      providesTags: ['Reports'],
      transformResponse: res => transformPaymentsReportsData(res),
    }),

    listTransactions: build.query({
      query: params => ({
        url: `incentivio-payments/payments/transactions`,
        params,
      }),
      providesTags: ['Transactions'],
    }),

    getTransaction: build.query<SingleTransaction, string>({
      query: id => ({
        url: `incentivio-payments/payments/transactions/${id}`,
      }),
      providesTags: result => [
        { type: 'Transaction', id: result.orderId },
        { type: 'GiftCardTransaction', id: result.giftcardTxId },
      ],
    }),

    listDisputes: build.query<
      { content: DisputeResponse[]; paging: Paging },
      any
    >({
      query: params => ({
        url: `incentivio-payments/disputes`,
        params,
      }),
      providesTags: ['Chargebacks'],
      transformResponse: res => transformDisputesData(res),
    }),

    acceptDispute: build.mutation({
      query: ({ disputeId }) => ({
        url: `incentivio-payments/disputes/${disputeId}/accept`,
        method: 'PUT',
      }),
      invalidatesTags: generateInvalidates(
        params => params.disputeId,
        'Chargeback',
      ),
    }),

    refundPayment: build.mutation({
      query: body => ({
        url: `payments/transactions/refund`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, body) => [
        'Transactions',
        {
          type: body.orderId ? 'Transaction' : 'GiftCardTransaction',
          id: body.orderId ?? body.giftcardTransactionId,
        },
      ],
    }),

    challengeDispute: build.mutation<any, UpdateDisputeRequest>({
      query: ({ disputeId, body }) => {
        const transformed = {
          ...body,
          evidence: transformEvidence(body.evidence),
        };

        return {
          url: `incentivio-payments/disputes/${disputeId}/submit`,
          method: 'POST',
          body: transformed,
        };
      },
      invalidatesTags: generateInvalidates(
        params => params.disputeId,
        'Chargeback',
      ),
    }),

    getFileLink: build.query<FileLink, { disputeId: string; fileId: string }>({
      query: ({ disputeId, fileId }) => ({
        url: `incentivio-payments/disputes/${disputeId}/file/${fileId}/retrieve`,
      }),
      providesTags: result => [
        { type: 'ChargebackFileLink', id: result?.fileId },
      ],
    }),

    getDispute: build.query<DisputeResponse, { disputeId: string }>({
      query: ({ disputeId }) => ({
        url: `incentivio-payments/disputes/${disputeId}`,
      }),
      providesTags: result => [
        { type: 'Chargeback', id: result?.stripeBody.id },
      ],
    }),
  }),
});

export const {
  useListAccountsQuery,
  useLazyListAccountsQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useCreateAccountLinkMutation,
  useDeleteAccountMutation,
  useGetAccountQuery,
  useGetPaymentsReportsDataQuery,
  useListTransactionsQuery,
  useGetTransactionQuery,
  useListDisputesQuery,
  useAcceptDisputeMutation,
  useRefundPaymentMutation,
  useChallengeDisputeMutation,
  useGetFileLinkQuery,
  useLazyGetFileLinkQuery,
  useGetDisputeQuery,
} = endpoints;
